/**
 * AI:
 * Status: "in progress"
 * Type: Widget
 * SubType: WidgetConfig
 * Reason: QL widget-table builder for the „Abrechnungskonten" of ONE `cr.CustomerBankAccount` in the read-only
 *         bank account overview OBC (`CrCustomerBankAccountOverviewObjectComponent`, requirement §5.4): one
 *         row per assignment SLICE `bm.CustomerBillingAccountBank` that points at the bank account - past,
 *         current and planned alike, closed accounts marked, newest slice first.
 *
 * ── THE QUERY IS THE LIVE-PROVEN ONE (FD-4, AU-4)
 *
 *   Same root, same join tree, same projection as `crBankAccountUsageQuery`
 *   (`cr/logic/cr-bank-account-usage-ql.ts`, run on dev/tenantA 28.09., L-4): root
 *   `bm.CustomerBillingAccountBank` as `e` WITHOUT a `fields` list (the whole 5-column slice, so
 *   `e.customerBillingAccountId` / `e.endDate` are in every row), LEFT join `customerBillingAccount` as `ba`
 *   with the reference's six columns, inside it LEFT join `billingRunType` as `brt`. The key space is
 *   therefore exactly the one `crReadBankAccountUsage` - and with it the cell logic of
 *   `cr/logic/cr-bank-account-billing-account-cell.ts` - reads. The spec compares the two builders, so a
 *   drift fails a test instead of emptying a column at runtime.
 *
 *   Two deliberate differences, both pinned by the spec:
 *   <ul>
 *     <li>the bank account restriction is a `fixedFilters` entry, not `filters`: the widget's filter UI owns
 *         `filters` and could drop it. Exactly ONE criterion, EQ on the Id-form FK `e.customerBankAccountId`
 *         - never IN/INN (on a ManyToOne column those are ignored in silence, the table would list every
 *         slice of every bank account);</li>
 *     <li>the sort: `e.startDate` descending, then `e.id` descending - newest slice first, on the ROOT only
 *         (a sort on a join alias the table cannot resolve returns zero rows).</li>
 *   </ul>
 *
 * ── THE COLUMNS (F4 default)
 *
 *   | # | Spalte           | Quelle                                                       | Full | Side |
 *   |---|------------------|--------------------------------------------------------------|------|------|
 *   | 1 | Abrechnungskonto | `#cust_billingAccount` (name / „Konto #id", „(geschlossen)") | ✓    | ✓    |
 *   | 2 | Laufart          | `brt.name`                                                   | ✓    | –    |
 *   | 3 | Zahlungsart      | `ba.paymentMethod`, NATIVE (text from the QL form EnumInfo)  | ✓    | –    |
 *   | 4 | Zugeordnet ab    | `e.startDate`                                                | ✓    | ✓    |
 *   | 5 | Zugeordnet bis   | `#cust_sliceEnd` („unbefristet" for 9999-12-31)              | ✓    | ✓    |
 *   | 6 | Status           | `#cust_sliceState` (Aktuell / Geplant ab … / Vergangen tag)  | ✓    | ✓    |
 *
 *   Zahlungsart is built natively on purpose (FD-5): live on dev/tenantA (17.10.2026) the QL form of this
 *   very query carries `ba.paymentMethod` as `dataType: Enum`, output `tag`, with its four-entry value list.
 *
 *   SIDE = Abrechnungskonto, Zugeordnet ab, Zugeordnet bis, Status. The drawer drops the two descriptive
 *   columns of the ACCOUNT (Laufart, Zahlungsart) and keeps everything that answers the question of this
 *   table - which account, and since/until when; the account itself is one click away (row click).
 *
 *   The three `#cust_` cells are rendered by the HOST through `mvs-widget-custom-field-directive` templates
 *   whose `fieldId` is the `fieldId` of {@link CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS} (the directive adds
 *   the `#cust_` prefix). Their values come from the projected row keys (`e.startDate`, `e.endDate`,
 *   `ba.name`, `ba.id`, `ba.closed`, `e.customerBillingAccountId`), folded by the pure cell functions.
 *
 * ── A STORED LAYOUT REPLACES ALL OF THIS (CANNOT 8)
 *
 *   A `ui.UiWidgetLayout` stored later on one of the two aliases REPLACES the code `widgetForm` outright
 *   (`WidgetService.buildUiData`: a non-empty stored layout wins over the `widgetForm` parameter). It then
 *   has to carry the three `#cust_` columns itself, or their cells disappear. It also NARROWS the projection
 *   to its visible fields plus every field ending in `id` (`MvsWidget.postInitRequest` select attributes):
 *   `e.endDate`, `ba.name` and `ba.closed` must stay visible or be marked `uiForceGet`, or the custom cells
 *   read nothing. Documented here, not prevented. The new aliases ship without a layout, so the code wins.
 *
 * ── DISPLAY ONLY AND IDENTITY
 *
 *   The four FIXED parameters of `buildDisplayOnlyListWidgetParams()` are IMPORTED (FD-3, AU-2); the host
 *   sets `[disableUpdate]="true"`. The two aliases are ALSO the QL query names (AU-13) and must never be
 *   renamed (FD-6). A builder cannot wire navigation: the host handles `(onObjectSelect)`, which reports the
 *   SLICE (`bm.CustomerBillingAccountBank`) - the account id comes from the row data.
 *
 * @ticket TM-1000002030
 */
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {WidgetForm} from 'features/core/shared/widget/widget-form';
import {WidgetConfigFormLayout} from 'features/core/shared/widget/widget-config-form-layout';
import {WidgetConfigFormField} from 'features/core/shared/widget/widget-config-form-field';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {QlQueryDto} from 'features/core/shared/ql/data/dto/ql-query.dto';
import {
    buildDisplayOnlyListWidgetParams
} from '../component/protected-components/cr-customer/component/cr-customer-standard-navigation/cr-customer-standard-navigation.component';
import {CR_BANK_ACCOUNT_USAGE_EMPTY_TEXT} from '../logic/cr-bank-account-usage-texts';

/** Widget alias AND QL query name of the full variant (AU-13). NEVER rename (FD-6). @ticket TM-1000002030 */
export const CR_BANK_ACCOUNT_BILLING_ACCOUNTS_ALIAS_FULL = 'cr.customerBankAccount.object.billingAccounts.full';

/** Widget alias AND QL query name of the side variant (AU-13). NEVER rename (FD-6). @ticket TM-1000002030 */
export const CR_BANK_ACCOUNT_BILLING_ACCOUNTS_ALIAS_SIDE = 'cr.customerBankAccount.object.billingAccounts.side';

/**
 * `MvsWidgetCustomFieldDirective.fieldPrefix` - a layout field with it renders a host template.
 *
 * @ticket TM-1000002030
 */
export const CR_BANK_ACCOUNT_CUSTOM_FIELD_PREFIX = '#cust_';

/**
 * The three `#cust_` columns, shared by this builder (layout field `#cust_<fieldId>`) and the host template
 * (`<ng-template mvs-widget-custom-field-directive [fieldId]="…fieldId" [label]="…label">`):
 * <ul>
 *   <li>`billingAccount` - „Abrechnungskonto": `ba.name`, else „Konto #id", plus „(geschlossen)";</li>
 *   <li>`sliceEnd` - „Zugeordnet bis": `e.endDate`, „unbefristet" for 9999-12-31 (E-7);</li>
 *   <li>`sliceState` - „Status": Aktuell / Geplant ab TT.MM.JJJJ / Vergangen against today (E-2).</li>
 * </ul>
 *
 * @ticket TM-1000002030
 */
export const CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS = {
    billingAccount: {fieldId: 'billingAccount', label: 'Abrechnungskonto'},
    sliceEnd: {fieldId: 'sliceEnd', label: 'Zugeordnet bis'},
    sliceState: {fieldId: 'sliceState', label: 'Status'},
} as const;

/**
 * One table column: the layout field id (`e.<f>` / `<joinAs>.<f>` / `#cust_<id>`) and its German header.
 *
 * @ticket TM-1000002030
 */
export interface CrBankAccountBillingAccountColumn {
    readonly field: string;
    readonly label: string;
}

function customColumn(column: { fieldId: string; label: string }): CrBankAccountBillingAccountColumn {
    return {field: CR_BANK_ACCOUNT_CUSTOM_FIELD_PREFIX + column.fieldId, label: column.label};
}

/**
 * Full view: all six columns, in order.
 *
 * @ticket TM-1000002030
 */
export const CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS_FULL: readonly CrBankAccountBillingAccountColumn[] = [
    customColumn(CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS.billingAccount),
    {field: 'brt.name', label: 'Laufart'},
    {field: 'ba.paymentMethod', label: 'Zahlungsart'},
    {field: 'e.startDate', label: 'Zugeordnet ab'},
    customColumn(CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS.sliceEnd),
    customColumn(CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS.sliceState),
];

/**
 * Side drawer: four columns - Abrechnungskonto, Zugeordnet ab, Zugeordnet bis, Status (F4 default; Laufart
 * and Zahlungsart dropped, see the file comment).
 *
 * @ticket TM-1000002030
 */
export const CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS_SIDE: readonly CrBankAccountBillingAccountColumn[] = [
    customColumn(CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS.billingAccount),
    {field: 'e.startDate', label: 'Zugeordnet ab'},
    customColumn(CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS.sliceEnd),
    customColumn(CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS.sliceState),
];

/**
 * Every slice `bm.CustomerBillingAccountBank` of ONE bank account, newest first.
 *
 * Key space of a row: `e.id`, `e.startDate`, `e.endDate`, `e.customerBillingAccountId`,
 * `e.customerBankAccountId`, `ba.id`, `ba.name`, `ba.paymentMethod`, `ba.closed`, `ba.frozen`,
 * `ba.sepaCollectionStatus`, `brt.id`, `brt.name`, `brt.shortName` - identical to `crBankAccountUsageQuery`.
 *
 * @param name          query name = pipe name; pass the widget alias (AU-13)
 * @param bankAccountId id of the `cr.CustomerBankAccount`
 * @ticket TM-1000002030
 */
export function buildCrBankAccountBillingAccountsQuery(name: string, bankAccountId: number): QlQueryDto {
    return {
        name: name,
        type: 'view',
        start: {
            type: 'entity',
            name: 'bm.CustomerBillingAccountBank',
            as: 'e',
            joins: [
                {
                    type: 'entity',
                    name: 'customerBillingAccount',
                    as: 'ba',
                    joinType: 'left',
                    fields: [{name: 'id'}, {name: 'name'}, {name: 'paymentMethod'}, {name: 'closed'},
                        {name: 'frozen'}, {name: 'sepaCollectionStatus'}],
                    joins: [
                        {
                            type: 'entity',
                            name: 'billingRunType',
                            as: 'brt',
                            joinType: 'left',
                            fields: [{name: 'id'}, {name: 'name'}, {name: 'shortName'}]
                        }
                    ]
                }
            ]
        },
        fixedFilters: [
            FilterCriteria.create('e.customerBankAccountId', FilterCriteria.cOperatorEqual, bankAccountId)
        ],
        sortings: [new Sorting('e.startDate', false), new Sorting('e.id', false)]
    };
}

/**
 * Curated column set + order + German headers, passed as the `widgetForm` parameter. `hideFields = true`
 * plus `fieldsConfigured = true` is the default-layout convention: only the configured fields are shown.
 * A fresh instance per call.
 *
 * @ticket TM-1000002030
 */
export function buildCrBankAccountBillingAccountsWidgetForm(
    columns: readonly CrBankAccountBillingAccountColumn[]): WidgetForm {

    const layout = new WidgetConfigFormLayout();
    layout.hideFields = true;
    layout.fieldsConfigured = true;

    columns.forEach((column, index) => {
        const field = WidgetConfigFormField.create(column.field, '', '');
        field.uiLabel = column.label;
        field.uiPriority = index;
        layout.addField(field);
    });

    const widgetForm = new WidgetForm();
    widgetForm.setDefaultLayout(layout);
    return widgetForm;
}

/**
 * The „Abrechnungskonten" table of one bank account.
 *
 * @param bankAccountId id of the `cr.CustomerBankAccount`; `null`/`undefined`/`0` returns `null` - a table
 *                      without the restriction would have to list every slice
 * @param compact       `true` for the side drawer (4 columns, paging 10, side alias), `false` for the full
 *                      view (6 columns, paging 25, full alias)
 * @returns the widget, or `null` without a usable id
 * @ticket TM-1000002030
 */
export function buildCrBankAccountBillingAccountsWidget(bankAccountId: number | null | undefined,
                                                        compact: boolean): WidgetData | null {

    if (!bankAccountId) {
        return null;
    }

    const alias: string = compact
        ? CR_BANK_ACCOUNT_BILLING_ACCOUNTS_ALIAS_SIDE
        : CR_BANK_ACCOUNT_BILLING_ACCOUNTS_ALIAS_FULL;

    /**
     * AI:
     * Status: "in progress"
     * Type: Widget
     * SubType: Runtime
     * Reason: Read-only table of the assignment slices bm.CustomerBillingAccountBank that use ONE
     *         cr.CustomerBankAccount - billing account, run type, payment method, assigned from/until and slice
     *         state, newest first; curated QL table (fixedFilters EQ on the bank account, LEFT joins ba → brt)
     *         with three #cust_ cells rendered by the host, row selection for the account navigation.
     */
    const widgetData: WidgetData = WidgetFactory.createWidgetTableQl(
        alias,
        'Abrechnungskonten',
        buildCrBankAccountBillingAccountsQuery(alias, bankAccountId),
        WidgetDataParam.create('size', 'S'),
        WidgetDataParam.create('paging', compact ? 10 : 25),
        WidgetDataParam.create('widgetForm', buildCrBankAccountBillingAccountsWidgetForm(
            compact ? CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS_SIDE : CR_BANK_ACCOUNT_BILLING_ACCOUNT_COLUMNS_FULL)),
        ...buildDisplayOnlyListWidgetParams());

    widgetData.setParamValue('selectionMode', 'rowSelect');
    widgetData.noDataText = CR_BANK_ACCOUNT_USAGE_EMPTY_TEXT;
    return widgetData;
}
